import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Typography,
  Space,
  Upload,
  Row,
  Col,
  Modal
} from "antd";
import {
  ArrowLeftOutlined,
  UploadOutlined,
  FileTextOutlined,
  SaveOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  createCourseModule,
  updateCourseModule
} from "../Services/CourseListService";
import { fetchModuleWithDetails } from "../Services/CourseListService";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const AddModulePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [moduleFileChanged, setModuleFileChanged] = useState(false);
  const [originalFileName, setOriginalFileName] = useState("");
  const [moduleData, setModuleData] = useState(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);


  // State management
  const queryParams = new URLSearchParams(location.search);
  const moduleId = queryParams.get("moduleId");
  const isEditMode = !!moduleId;

  // Load module data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadModuleData();
    }
  }, [moduleId]);

  const loadModuleData = async () => {
    try {
      setLoading(true);
      console.log(" Loading module data for ID:", moduleId);

      const data = await fetchModuleWithDetails(moduleId);
      setModuleData(data);

      console.log(" Module data loaded from API:", data);

      // Set form values with actual API data
      form.setFieldsValue({
        title: data.title || "",
        description: data.description || "",
        order: data.order || 1
      });

      // Set original file information
      if (data.fileName) {
        setOriginalFileName(data.fileName);
        setHasExistingFile(true);

      }

    } catch (error) {
  const backend = error?.response?.data;
  const backendMessage =
    (backend && typeof backend === "object" ? backend.data || backend.message : backend) ||
    "Failed to load module data";

  toast.error(backendMessage);

 navigate(`/proAdmin-dashboard/courseDetails/${courseId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (info) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1); // Only allow one file

    newFileList = newFileList.map(file => {
      if (file.originFileObj) {
        return file;
      }
      return {
        ...file,
        originFileObj: file
      };
    });

    setFileList(newFileList);

    // If a file is uploaded and it's different from original, set changed flag
    if (newFileList.length > 0 && info.file.status === 'done') {
      toast.success(`${info.file.name} file uploaded successfully`);
    }
  };

  const handleFileRemove = () => {
    if (isEditMode && hasExistingFile) {
      confirm({
        title: 'Remove Module File?',
        icon: <ExclamationCircleOutlined />,
        content: `Are you sure you want to remove the current file "${originalFileName}"? This action cannot be undone.`,
        okText: 'Yes, Remove File',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          setFileList([]);
          setModuleFileChanged(true);
          setHasExistingFile(false);
          toast.info("File will be removed when you update the module");
        }
      });
    } else {
      setFileList([]);
      setModuleFileChanged(true);
    }
    return false;
  };

  const beforeUpload = (file) => {
 const isPDF = file.type === "application/pdf";
  if (!isPDF) {
    toast.error("Only PDF files are allowed!");
    return Upload.LIST_IGNORE; 
  }

    const isLt10M = file.size / 1024 / 1024 < 15;
    if (!isLt10M) {
      toast.error('File must be smaller than 15MB!');
      return false;
    }

    // For edit mode, show confirmation when replacing existing file
    if (isEditMode && hasExistingFile && !moduleFileChanged) {
      confirm({
        title: 'Replace Current File?',
        icon: <ExclamationCircleOutlined />,
        content: `You are about to replace the current file "${originalFileName}". Do you want to continue?`,
        okText: 'Yes, Replace',
        okType: 'primary',
        cancelText: 'Cancel',
        onOk() {
          setModuleFileChanged(true);
          return true; // Allow upload to proceed
        },
        onCancel() {
          return false; // Prevent upload
        },
      });
    } else {
      setModuleFileChanged(true);
      return true; // Allow upload to proceed
    }
  };

  const uploadProps = {
    beforeUpload,
    onChange: handleFileUpload,
    fileList,
    multiple: false,
    accept: ".pdf",
    customRequest: ({ onSuccess }) => {
      setTimeout(() => onSuccess('ok'), 0);
    },
    onRemove: handleFileRemove,
    showUploadList: {
      showDownloadIcon: true,
      showRemoveIcon: true,
    }
  };

  const handleModuleSubmit = async (values) => {
    // For edit mode, file is optional
    if (!isEditMode && (!fileList || fileList.length === 0)) {
      toast.warning("Please upload a module file");
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        // Update existing module
        const updateData = {
          moduleId: parseInt(moduleId),
          moduleFileChanged: moduleFileChanged, // This will be true if file was changed/removed
          title: values.title?.trim() || "",
          description: values.description?.trim() || "",
        };

        // Only add moduleFile if it's changed and a new file is selected
        if (moduleFileChanged && fileList.length > 0) {
          updateData.moduleFile = fileList[0]?.originFileObj || fileList[0];
        }

        console.log(" Update data to API:", {
          moduleId: updateData.moduleId,
          moduleFileChanged: updateData.moduleFileChanged,
          hasFile: !!updateData.moduleFile,
          title: updateData.title,
          description: updateData.description
        });

        await updateCourseModule(updateData);

        if (moduleFileChanged) {
          if (fileList.length > 0) {
            toast.success("Module updated successfully with new file!");
          } else {
            toast.success("Module updated successfully! File has been removed.");
          }
        } else {
          toast.success("Module updated successfully!");
        }

        // Navigate back to module details
        setTimeout(() => {
          navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
        }, 1500);

      } else {
        // Create new module
        const fileObj = fileList[0].originFileObj || fileList[0];
        if (!(fileObj instanceof File)) {
          throw new Error("Invalid file object");
        }

        const moduleData = {
          courseId: parseInt(courseId),
          title: values.title?.trim() || "",
          description: values.description?.trim() || "",
          order: parseInt(values.order) || 0,
          moduleFile: fileObj,
        };

        await createCourseModule(moduleData);
        toast.success("Module created successfully!");

        // Navigate back to course
        setTimeout(() => {
          navigate(`/proAdmin-dashboard/courseDetails/${courseId}`);
        }, 1500);
      }
} catch (error) {
  console.log("AXIOS ERROR >>>", error.response);

  const backend = error?.response?.data;

  let finalMessage = "";

  // Backend returns object with { message, status, data }
  if (backend && typeof backend === "object") {
    if (backend.data) {
      finalMessage = backend.data;  // ⬅ GET THIS FIELD
    } else if (backend.message) {
      finalMessage = backend.message;
    } else {
      finalMessage = JSON.stringify(backend);
    }
  }

  // If backend returned a plain string
  else if (typeof backend === "string") {
    finalMessage = backend;
  }

  // Fallback
  else {
    finalMessage = error.message || "Something went wrong";
  }

  toast.error(finalMessage);
}

 finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (isEditMode) {
      navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
    } else {
      navigate(`/proAdmin-dashboard/courseDetails/${courseId}`);
    }
  };

  const getHeaderTitle = () => {
    return isEditMode ? "Edit Module" : "Add New Module";
  };

  const getSubmitButtonText = () => {
    return isEditMode ? "Update Module" : "Create Module";
  };

  const getSubmitButtonIcon = () => {
    return isEditMode ? <SaveOutlined /> : <SaveOutlined />;
  };

  const getFileUploadDescription = () => {
    if (isEditMode) {
      if (hasExistingFile) {
        return `Current file: ${originalFileName}. Upload a new file to replace it.`;
      } else {
        return "No current file. Upload a file to add one.";
      }
    }
    return "Upload learning materials (PDF - max 15MB)";
  };

  const getFileUploadStatus = () => {
    if (!isEditMode) return null;

    if (moduleFileChanged) {
      if (fileList.length > 0) {
        return (
          <div style={{ marginTop: 8, padding: 8, background: '#F6EFDD', borderRadius: 4 }}>
            <Text type="secondary">New file selected - will replace current file on update</Text>
          </div>
        );
      } else {
        return (
          <div style={{ marginTop: 8, padding: 8, background: '#fff2e8', borderRadius: 4 }}>
            <Text type="secondary">File will be removed on update</Text>
          </div>
        );
      }
    }
    return null;
  };

  return (



    <Card className="card-professional"
      title={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Title level={2} style={{ margin: 0, color: 'white' }}>
            {getHeaderTitle()}
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
            Course ID: {courseId}
            {moduleId && ` | Module ID: ${moduleId}`}
          </Text>
        </Space>
      }
      extra={
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}

        >
          {isEditMode ? "Back to Module" : "Back to Course"}
        </Button>

      }
    >



      {/* Module Form */}
      <div
        style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #f0f0f0' }}
        title={
          <Space>
            <FileTextOutlined />
            <Text strong>{isEditMode ? 'Edit Module' : 'Module Information'}</Text>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModuleSubmit}
          requiredMark="optional"
          initialValues={{
            order: 1
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Module Title"
                rules={[
                  { required: true, message: "Please enter module title" },
                  { min: 3, message: "Title must be at least 3 characters" }
                ]}
              >
                <Input placeholder="Enter module title" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="order"
                label="Module Order"
                rules={[
                  { required: !isEditMode, message: "Please enter module order" },
                  { type: 'number', min: 1, message: 'Order must be at least 1' }
                ]}
              >
                <InputNumber
                  placeholder="Sequence number"
                  style={{ width: '100%' }}
                  size="large"
                  min={1}
                  disabled={isEditMode}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Module Description"
            rules={[
              { required: true, message: "Please enter module description" },
              { min: 10, message: "Description must be at least 10 characters" }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe what this module covers and what students will learn..."
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item
            label="Module File"
            required={!isEditMode}
            extra={getFileUploadDescription()}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} size="large">
                {isEditMode ? "Upload New File" : "Click to Upload"}
              </Button>
            </Upload>
            {getFileUploadStatus()}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                icon={getSubmitButtonIcon()}
              >
                {getSubmitButtonText()}
              </Button>
              <Button
                onClick={handleBack}
                size="large"
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Card>
  );
};

export default AddModulePage;